/**
 * The trees report reads its GeoPackages once, here, so the map and every statistic share them.
 * The crowns stay in their worker as tiles (vectorSource.ts); the page gets one point per tree.
 * A boundary that cannot be read is dropped quietly: the area falls back to the crowns' hull.
 * Without a crowns ref only the boundary is read (the crowns then come from their tile archive).
 */
import { useEffect, useMemo, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import type { LayerRef } from '@/map/layers';
import type { LinkSource } from '@/map/linkSource';
import { loadLinkedCrownLayer, loadLinkedGpkgLayer, VectorSourceError, type CrownServer, type VectorProgress } from '@/map/vectorSource';
import type { Threshold } from './config';

export interface TreesData {
  /** Centroids with the properties the statistics read. */
  trees: FeatureCollection | null;
  boundary: FeatureCollection | null;
  treesObject?: string;
  boundaryObject?: string;
  /** Hull area of the crowns, for the stand area when no boundary is bound. */
  hullHa?: number;
  /** Where the crowns are; `retag` after a thresholds change. */
  server?: CrownServer;
  loading: boolean;
  failed: boolean;
}

interface Loaded {
  key: string;
  trees: FeatureCollection | null;
  boundary: FeatureCollection | null;
  treesObject?: string;
  boundaryObject?: string;
  hullHa?: number;
  server?: CrownServer;
  failed: boolean;
}

export function useTreesData(
  refs: LayerRef[],
  linkSource: LinkSource,
  onError?: (object: string, e: VectorSourceError) => void,
  onProgress?: (object: string, progress: VectorProgress | null) => void,
  thresholds: Threshold[] = [],
): TreesData {
  const treesRef = refs.find((r) => r.via === 'link' && r.object_kind === 'ortho_tree_analysis');
  const boundaryRef = refs.find((r) => r.via === 'link' && r.object_kind === 'boundary');
  const key = JSON.stringify([treesRef, boundaryRef]);
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    const [t, b] = JSON.parse(key) as [LayerRef | null, LayerRef | null];
    if (!t && !b) return;
    let alive = true;
    const progressOf = (object: string) => (p: VectorProgress) => alive && onProgress?.(object, p);
    const trees = t ? loadLinkedCrownLayer(t, linkSource, { onProgress: progressOf(t.object), thresholds }).finally(() => alive && onProgress?.(t.object, null)) : Promise.resolve(null);
    const boundary = b ? loadLinkedGpkgLayer(b, linkSource, { onProgress: progressOf(b.object) }).catch(() => null).finally(() => alive && onProgress?.(b.object, null)) : Promise.resolve(null);
    Promise.all([trees, boundary])
      .then(([c, bf]) => {
        if (alive) setLoaded({ key, trees: c?.points ?? null, boundary: bf, treesObject: t?.object, boundaryObject: bf ? b?.object : undefined, hullHa: c?.hullHa, server: c?.server, failed: false });
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setLoaded({ key, trees: null, boundary: null, treesObject: t?.object, failed: true });
        if (t) onError?.(t.object, e instanceof VectorSourceError ? e : new VectorSourceError('fetch', String(e)));
      });
    return () => {
      alive = false;
    };
    // onError and onProgress are callback props, and the thresholds only seed the first tags
    // (TreesReport retags on change); none of them may reload the files.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, linkSource]);

  const treesObject = treesRef?.object;
  const wanted = !!(treesRef || boundaryRef);
  return useMemo<TreesData>(() => {
    if (!wanted) return { trees: null, boundary: null, loading: false, failed: false };
    if (!loaded || loaded.key !== key) return { trees: null, boundary: null, treesObject, loading: true, failed: false };
    const { trees, boundary, treesObject: to, boundaryObject, hullHa, server, failed } = loaded;
    return { trees, boundary, treesObject: to, boundaryObject, hullHa, server, loading: false, failed };
  }, [wanted, treesObject, loaded, key]);
}
